import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { sendRequest } from '../../utils/dateHelpers';
import HistoricalAnalysisV2View from './HistoricalAnalysisV2View';

const API_URL = '/historical-analysis/requests';
const POLL_STATUSES = new Set([
  'pending_estimate',
  'estimating',
  'payment_pending',
  'processing_queued',
  'processing'
]);
const ACTIVE_STATUSES = new Set([
  'pending_estimate',
  'estimating',
  'estimate_ready',
  'payment_failed',
  'payment_pending',
  'processing_queued',
  'processing',
  'processing_failed'
]);

const HistoricalAnalysisV2 = ({ initialPayload = null }) => {
  const [payload, setPayload] = useState(initialPayload || { requests: [] });
  const [loading, setLoading] = useState(!initialPayload);
  const [busy, setBusy] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(null);
  const [reviewedOfferKey, setReviewedOfferKey] = useState(null);
  const purchaseInFlight = useRef(false);
  const [confirmingRequestId, setConfirmingRequestId] = useState(null);

  const request = useMemo(() => {
    const requests = (payload.requests || [])
      .filter(item => Number(item.flowVersion) === 2)
      .sort(
        (left, right) =>
          new Date(right.createdAt || 0).getTime() -
          new Date(left.createdAt || 0).getTime()
      );
    return (
      requests.find(item => ACTIVE_STATUSES.has(item.status)) ||
      requests[0] ||
      null
    );
  }, [payload.requests]);
  const offer =
    request?.offers?.find(item => item.months === selectedMonths) || null;
  const creditCents = Number(
    payload.credit?.availableCents ?? request?.credit?.availableCents ?? 0
  );
  const offerKey = JSON.stringify([
    request?.id,
    request?.status,
    request?.expiresAt,
    creditCents,
    offer?.months,
    offer?.startDate,
    offer?.endDate,
    offer?.commitCount,
    offer?.baseAmountCents,
    offer?.discountPercentage,
    offer?.discountAmountCents,
    offer?.subtotalCents,
    Number(offer?.creditAppliedCents || 0),
    offer?.payableAmountCents
  ]);
  const reviewOpen = Boolean(offer && reviewedOfferKey === offerKey);
  const closeReview = useCallback(() => {
    if (!purchaseInFlight.current) setReviewedOfferKey(null);
  }, []);

  // A changed quote must be reviewed again before it can be purchased.
  useEffect(() => {
    if (reviewedOfferKey !== null && reviewedOfferKey !== offerKey) {
      setReviewedOfferKey(null);
    }
  }, [offerKey, reviewedOfferKey]);

  const excludedRepositories = useMemo(
    () =>
      (request?.discoveryProgress?.repositories || []).filter(
        repo => repo.status === 'excluded'
      ),
    [request?.discoveryProgress?.repositories]
  );
  const supportUrl = useMemo(() => {
    if (!request?.id || excludedRepositories.length === 0) return '';
    const repoNames = excludedRepositories
      .map(repo => repo.repoName)
      .filter(Boolean)
      .join(', ');
    const params = new URLSearchParams({
      topic: 'historical_analysis',
      message: `Some repositories were not included while preparing our Historical Analysis offers: ${repoNames}. Please help us review their access. Reference: ${request.id}`
    });
    return `/settings/support?${params.toString()}`;
  }, [excludedRepositories, request?.id]);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await sendRequest(API_URL, 'GET');
      setPayload(response || { requests: [] });
      setConfirmingRequestId(current => {
        const confirmedRequest = response?.requests?.find(
          item => item.id === current
        );
        // A stale quote does not confirm whether the purchase was accepted.
        return confirmedRequest && confirmedRequest.status !== 'estimate_ready'
          ? null
          : current;
      });
    } catch (error) {
      toast.error(error?.message || 'Historical analysis could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialPayload) setPayload(initialPayload);
    else load();
  }, [initialPayload, load]);

  useEffect(() => {
    if (
      (!request || !POLL_STATUSES.has(request.status)) &&
      !confirmingRequestId
    )
      return undefined;
    const timer = window.setInterval(() => load(true), 15000);
    return () => window.clearInterval(timer);
  }, [load, request, confirmingRequestId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') !== 'success') return;
    const returnedRequestId = params.get('request_id');
    if (!returnedRequestId || !request || returnedRequestId === request.id) {
      params.delete('checkout');
      params.delete('request_id');
      const query = params.toString();
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
      );
      const pendingId = returnedRequestId || request?.id;
      if (pendingId) setConfirmingRequestId(pendingId);
      load(true);
    }
  }, [load, request]);

  useEffect(() => {
    setSelectedMonths(null);
    setReviewedOfferKey(null);
  }, [request?.id]);

  const prepare = async () => {
    try {
      setBusy(true);
      await sendRequest(API_URL, 'POST', {});
      await load(true);
    } catch (error) {
      toast.error(error?.message || 'Offers could not be prepared.');
    } finally {
      setBusy(false);
    }
  };

  const purchase = async () => {
    if (
      !offer ||
      !reviewOpen ||
      busy ||
      confirmingRequestId ||
      purchaseInFlight.current ||
      !['estimate_ready', 'payment_failed'].includes(request?.status)
    )
      return;
    purchaseInFlight.current = true;
    setConfirmingRequestId(request.id);
    try {
      setBusy(true);
      const response = await sendRequest(
        `${API_URL}/${request.id}/purchase`,
        'POST',
        {
          months: offer.months
        }
      );
      if (response?.paymentMode === 'checkout' && response.checkoutUrl) {
        window.location.assign(response.checkoutUrl);
        return;
      }
      toast.success(
        response?.paymentMode === 'no_charge'
          ? 'Balance applied. Historical analysis is starting.'
          : 'Payment submitted. Analysis will start after confirmation.'
      );
      await load(true);
    } catch (error) {
      toast.error(error?.message || 'Purchase could not be started.');
      await load(true);
    } finally {
      purchaseInFlight.current = false;
      setBusy(false);
      setReviewedOfferKey(null);
    }
  };

  return (
    <HistoricalAnalysisV2View
      loading={loading}
      request={
        confirmingRequestId && (!request || confirmingRequestId === request.id)
          ? {
              ...request,
              id: confirmingRequestId,
              status: 'payment_pending',
              payment: { state: 'outcome_unknown' }
            }
          : request
      }
      offer={offer}
      creditCents={creditCents}
      selectedMonths={selectedMonths}
      reviewOpen={reviewOpen}
      busy={busy}
      excludedRepositories={excludedRepositories}
      supportUrl={supportUrl}
      onPrepare={prepare}
      onPurchase={purchase}
      onSelect={months => {
        setSelectedMonths(months);
        setReviewedOfferKey(null);
      }}
      onReview={() => {
        if (offer && !busy && !confirmingRequestId)
          setReviewedOfferKey(offerKey);
      }}
      onChangePeriod={closeReview}
    />
  );
};

export default HistoricalAnalysisV2;

HistoricalAnalysisV2.propTypes = {
  initialPayload: PropTypes.shape({
    requests: PropTypes.arrayOf(PropTypes.object)
  })
};
